import React, { useState } from "react";
import { Typography, Button, TextField, Grid } from "@mui/material";
import {
  StyledCard,
  CardHeader,
  TopBtnBox,
  CardContent,
  LoadInfoItem,
  LoadLabel,
  LoadValue,
} from "./styles";
import {
  useDeleteMachineMaster,
  usePutData,
  usePutMachineMaster,
} from "../Plan/tableHooks";
import { DeleteButton, EditButton } from "../../common/Buttons";

function EquipmentCard({
  equipment,
  setUpdateStatus,
  group_code,
  project_code,
  openSnackbar,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(equipment.name);
  const [editedBaseLoad, setEditedBaseLoad] = useState(
    equipment.base_load || 0
  );
  const [warningLoad, setWarningLoad] = useState(equipment.warning_load || 0);
  const [editedMaxLoad, setEditedMaxLoad] = useState(equipment.max_load || 0);
  const deleteMchUsableProc = useDeleteMachineMaster(
    null,
    "/api/mch/deleteMchM"
  );
  const updateMutation = usePutData(
    null,
    `/api/mch/putOriginalName`,
    openSnackbar
  );
  const deleteMchMaster = async (machine_id) => {
    try {
      await deleteMchUsableProc.mutateAsync({
        machine_id,
        group_code,
        project_code,
      });
      setUpdateStatus((prev) => !prev);
    } catch (e) {
      console.log(e);
    }
  };
  const handleEditStart = () => {
    setIsEditing(true);
  };
  const updateMachineMaster = usePutMachineMaster(null, "/api/mch/updateMchM");
  const handleEditComplete = async () => {
    try {
      const arr = [
        group_code,
        project_code,
        equipment.machine_id,
        editedName,
        parseFloat(editedBaseLoad) || 0,
        parseFloat(warningLoad) || 0,
        parseFloat(editedMaxLoad) || 0,
      ];
      await updateMachineMaster.mutateAsync(arr, {
        onSuccess: () => {
          setIsEditing(false);
          setUpdateStatus((prev) => !prev);
          openSnackbar("장비 정보가 성공적으로 수정되었습니다.");
        },
        onError: (e) => {
          console.log(e);
          openSnackbar("장비 정보 수정 중 오류가 발생했습니다.", "error");
        },
      });
    } catch (e) {
      console.log(e);
      openSnackbar("장비 정보 수정 중 오류가 발생했습니다.", "error");
    }
  };
  const onClickUseOriginalName = async (machine_id) => {
    const obj = {
      group_code,
      project_code,
      data: machine_id,
    };
    await updateMutation.mutateAsync(obj, {
      onSuccess: () => {
        setIsEditing(false);
        setUpdateStatus((prev) => !prev);
      },
    });
  };
  const validateLoadInput = (value) => {
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue >= 0;
  };
  const handleBaseLoadChange = (e) => {
    const value = e.target.value;
    if (value === "" || validateLoadInput(value)) {
      setEditedBaseLoad(value);
    }
  };
  const handleWarningLoadChange = (e) => {
    const value = e.target.value;
    if (value === "" || validateLoadInput(value)) {
      setWarningLoad(value);
    }
  };
  const handleMaxLoadChange = (e) => {
    const value = e.target.value;
    if (value === "" || validateLoadInput(value)) {
      setEditedMaxLoad(value);
    }
  };
  return (
    <StyledCard>
      <CardHeader>
        {isEditing ? (
          <TextField
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            sx={{ maxWidth: "45%" }}
          />
        ) : (
          <div
            style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}
          >
            <Typography variant="h5">{equipment.name}</Typography>
            {equipment.original_name && (
              <Typography variant="h7">({equipment.original_name})</Typography>
            )}
          </div>
        )}
        <TopBtnBox>
          {isEditing ? (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {equipment.original_name &&
                equipment.original_name !== equipment.name && (
                  <Button
                    onClick={() => onClickUseOriginalName(equipment.machine_id)}
                  >
                    원명사용?
                  </Button>
                )}
              <Button
                variant="contained"
                color="success"
                onClick={handleEditComplete}
                size="small"
              >
                수정완료
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsEditing(false);
                  setEditedName(equipment.name);
                  setEditedBaseLoad(equipment.base_load || 0);
                  setEditedMaxLoad(equipment.max_load || 0);
                }}
                size="small"
              >
                수정취소
              </Button>
            </div>
          ) : (
            <EditButton
              variant="contained"
              color="secondary"
              onClick={handleEditStart}
            />
          )}
          {!isEditing && (
            <DeleteButton
              variant="contained"
              color="error"
              onClick={() => deleteMchMaster(equipment.machine_id)}
            />
          )}
        </TopBtnBox>
      </CardHeader>
      {/* 부하율 정보 섹션 */}
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <LoadInfoItem>
              <LoadLabel>기준 부하율</LoadLabel>
              {isEditing ? (
                <TextField
                  value={editedBaseLoad}
                  onChange={handleBaseLoadChange}
                  variant="outlined"
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>,
                    inputProps: { min: 0, max: 100 },
                  }}
                  fullWidth
                />
              ) : (
                <LoadValue>{equipment.base_load || 0}%</LoadValue>
              )}
            </LoadInfoItem>
          </Grid>
          <Grid item xs={4}>
            <LoadInfoItem>
              <LoadLabel>경고 부하율</LoadLabel>
              {isEditing ? (
                <TextField
                  value={warningLoad}
                  onChange={handleWarningLoadChange}
                  variant="outlined"
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>,
                    inputProps: { min: 0, max: 100 },
                  }}
                  fullWidth
                />
              ) : (
                <LoadValue>{equipment.warning_load || 0}%</LoadValue>
              )}
            </LoadInfoItem>
          </Grid>
          <Grid item xs={4}>
            <LoadInfoItem>
              <LoadLabel>최대 부하율</LoadLabel>
              {isEditing ? (
                <TextField
                  value={editedMaxLoad}
                  onChange={handleMaxLoadChange}
                  variant="outlined"
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>,
                    inputProps: { min: 0, max: 100 },
                  }}
                  fullWidth
                />
              ) : (
                <LoadValue>{equipment.max_load || 0}%</LoadValue>
              )}
            </LoadInfoItem>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
}

export default EquipmentCard;
